import React, { useEffect, useState } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import HomeNav from '../_components/HomeNav'
import Footer from '../_components/Footer'

// ── Constants ─────────────────────────────────────────────────────────────────
const BRAND        = '#6eb446'
const BRAND_SHADOW = '0 8px 24px rgba(110,180,70,0.3)'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

// ── Password strength bar ─────────────────────────────────────────────────────
const checks = [
  { re: /.{8,}/,               label: 'At least 8 characters' },
  { re: /[a-z]/,               label: 'One lowercase letter' },
  { re: /[A-Z]/,               label: 'One uppercase letter' },
  { re: /\d/,                  label: 'One number' },
  { re: /[@$!%*#?&.¡,+¿()]+/, label: 'One special character' },
]

const StrengthBar: React.FC<{ password: string }> = ({ password }) => {
  const passed = checks.filter(c => c.re.test(password)).length
  const colors = ['#ef4444', '#f97316', '#eab308', '#84cc16', BRAND]
  const color  = password.length === 0 ? '#e2e8f0' : (colors[passed - 1] ?? '#e2e8f0')

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        {checks.map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 9999,
            backgroundColor: i < passed ? color : '#e2e8f0',
            transition: 'background-color 0.2s',
          }} />
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {checks.map((c, i) => (
          <span key={i} style={{
            fontSize: '0.72rem',
            color: c.re.test(password) ? '#166534' : '#94a3b8',
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <span>{c.re.test(password) ? '✓' : '○'}</span> {c.label}
          </span>
        ))}
      </div>
    </div>
  )
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
const Skeleton: React.FC = () => (
  <>
    <style>{`
      @keyframes shimmer {
        0%   { background-position: -600px 0; }
        100% { background-position:  600px 0; }
      }
      .shimmer {
        background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
        background-size: 600px 100%;
        animation: shimmer 1.4s ease-in-out infinite;
        border-radius: 10px;
      }
    `}</style>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="shimmer" style={{ height: 28, width: '60%', margin: '0 auto' }} />
      <div className="shimmer" style={{ height: 48 }} />
      <div className="shimmer" style={{ height: 48 }} />
      <div className="shimmer" style={{ height: 48 }} />
    </div>
  </>
)

// ── UpdatePassword ────────────────────────────────────────────────────────────
const UpdatePassword: React.FC = () => {
  const { t }                  = useTranslation()
  const { email, passwordkey } = useParams<{ email: string; passwordkey: string }>()
  const navigate               = useNavigate()

  const [ready, setReady]       = useState(false)
  const [valid, setValid]       = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [loading, setLoading]   = useState(false)
  const [showPass, setShowPass] = useState(false)
  const [showConf, setShowConf] = useState(false)

  useEffect(() => {
    document.body.classList.add('page-home')
    return () => document.body.classList.remove('page-home')
  }, [])

  useEffect(() => {
    const verify = async () => {
      try {
        await axios.get(`${url}auth/update-password/${email}/${passwordkey}`)
        setValid(true)
      } catch (err: any) {
        setErrorMsg(err?.response?.data?.response || 'Invalid or expired link.')
        setValid(false)
      } finally {
        setReady(true)
      }
    }
    verify()
  }, [email, passwordkey])

  const formSchema = Yup.object().shape({
    password: Yup.string()
      .min(8, 'At least 8 characters')
      .matches(/[a-z]+/, 'One lowercase character')
      .matches(/[A-Z]+/, 'One uppercase character')
      .matches(/[@$!%*#?&.¡,+¿()]+/, 'One special character')
      .matches(/\d+/, 'One number')
      .required(),
    passwordConfirm: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required(),
  })

  const updatePassword = async (params: { password: string; passwordConfirm: string }) => {
    setLoading(true)
    try {
      const res = await axios.post(`${url}auth/update-password/${email}/${passwordkey}`, params)
      Swal.fire({ icon: 'success', title: 'Success', text: res?.data?.response })
      navigate('/auth/login')
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    outline: 'none',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    transition: 'border-color 0.15s',
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: 6,
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#374151',
  }

  const eyeBtn: React.CSSProperties = {
    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#94a3b8', fontSize: '0.85rem', padding: 4,
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f8faf6' }}>
      <HomeNav />

      <style>{`
        .up-input:focus {
          border-color: ${BRAND} !important;
          box-shadow: 0 0 0 3px rgba(110,180,70,0.12) !important;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem 1.5rem' }}>
        <div style={{
          width: '100%',
          maxWidth: 440,
          backgroundColor: '#fff',
          borderRadius: 24,
          boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9',
          overflow: 'hidden',
        }}>

          {/* Top accent */}
          <div style={{ height: 4, backgroundColor: BRAND }} />

          <div style={{ padding: '2.25rem 2rem 2rem' }}>

            {/* Logo + heading */}
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <img
                src="https://labeldictate.com/assets/img/Label-Dictate-Logo-V2021.png"
                width={56}
                alt="Label Dictate"
                style={{ marginBottom: '1rem' }}
              />
              <h1 style={{
                margin: 0,
                fontSize: '1.4rem',
                fontWeight: 800,
                color: '#1e293b',
                letterSpacing: '-0.01em',
              }}>
                {t('login.update_password')}
              </h1>
            </div>

            {/* States */}
            {!ready ? (
              <Skeleton />
            ) : !valid ? (
              /* Invalid / expired link */
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  padding: '1.5rem',
                  backgroundColor: '#fef2f2',
                  borderRadius: 12,
                  border: '1px solid #fecaca',
                  marginBottom: '1.5rem',
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🔗</div>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#991b1b', fontWeight: 600 }}>
                    {errorMsg}
                  </p>
                </div>
              </div>
            ) : (
              /* Valid link — show form */
              <Formik
                initialValues={{ password: '', passwordConfirm: '' }}
                validationSchema={formSchema}
                onSubmit={updatePassword}
              >
                {({ values }) => (
                  <Form noValidate style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                    {/* New password */}
                    <div>
                      <label htmlFor="password" style={labelStyle}>{t('login.password')}</label>
                      <div style={{ position: 'relative' }}>
                        <Field
                          id="password" name="password"
                          type={showPass ? 'text' : 'password'}
                          placeholder="••••••••"
                          className="up-input"
                          style={{ ...inputStyle, paddingRight: 42 }}
                        />
                        <button type="button" onClick={() => setShowPass(v => !v)} style={eyeBtn}>
                          {showPass ? '🙈' : '👁'}
                        </button>
                      </div>
                      <ErrorMessage name="password" render={msg => (
                        <span style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: 4, display: 'block' }}>{msg}</span>
                      )} />
                      {values.password.length > 0 && <StrengthBar password={values.password} />}
                    </div>

                    {/* Confirm password */}
                    <div>
                      <label htmlFor="passwordConfirm" style={labelStyle}>{t('login.pass_confirm')}</label>
                      <div style={{ position: 'relative' }}>
                        <Field
                          id="passwordConfirm" name="passwordConfirm"
                          type={showConf ? 'text' : 'password'}
                          placeholder="••••••••"
                          className="up-input"
                          style={{ ...inputStyle, paddingRight: 42 }}
                        />
                        <button type="button" onClick={() => setShowConf(v => !v)} style={eyeBtn}>
                          {showConf ? '🙈' : '👁'}
                        </button>
                      </div>
                      <ErrorMessage name="passwordConfirm" render={msg => (
                        <span style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: 4, display: 'block' }}>{msg}</span>
                      )} />
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        width: '100%',
                        padding: '13px',
                        borderRadius: 9999,
                        border: 'none',
                        backgroundColor: loading ? '#a3d08a' : BRAND,
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        boxShadow: BRAND_SHADOW,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        marginTop: 4,
                        transition: 'filter 0.15s',
                      }}
                      onMouseEnter={e => { if (!loading) e.currentTarget.style.filter = 'brightness(1.08)' }}
                      onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)' }}
                    >
                      {loading && (
                        <span style={{
                          width: 16, height: 16, borderRadius: '50%',
                          border: '2px solid rgba(255,255,255,0.4)',
                          borderTopColor: '#fff',
                          display: 'inline-block',
                          animation: 'spin 0.7s linear infinite',
                        }} />
                      )}
                      {t('login.update_password')}
                    </button>

                  </Form>
                )}
              </Formik>
            )}

            {/* Back to login */}
            {ready && (
              <>
                <Link
                  to="/auth/login"
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '12px',
                    borderRadius: 9999,
                    border: `1.5px solid ${BRAND}`,
                    color: BRAND,
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    textDecoration: 'none',
                    textAlign: 'center',
                    boxSizing: 'border-box',
                    transition: 'background-color 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f4fbf0' }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent' }}
                >
                  {/* {t('login.back_to_login') || 'Regresar'} */}
                  Regresar
                </Link>
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default UpdatePassword
